import { useEffect, useState, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { 
  Search, Eye, CheckCircle2, XCircle, AlertTriangle, Filter, Trash2, 
  Video, FileText, Mic, User, Clock, Award, ChevronDown, ChevronUp,
  Play, Download, ExternalLink, Shield, EyeIcon, Users, Fingerprint,
  Activity, Brain, Waves, MonitorX, ScanFace, HeartPulse, AlertCircle,
  Target, Copy, Sparkles, MessageSquare, Star, Loader2, FolderArchive,
  RefreshCw, Send
} from 'lucide-react';

// Helper function to convert Google Drive view URL to download URL
const getGoogleDriveDownloadUrl = (url) => {
  if (!url) return null;
  
  // If it's already a download URL, return as is
  if (url.includes('export=download')) return url;
  
  // Extract file ID from various Google Drive URL formats
  let fileId = null;
  
  // Format: https://drive.google.com/file/d/{FILE_ID}/view
  const viewMatch = url.match(/\/file\/d\/([a-zA-Z0-9_-]+)/);
  if (viewMatch) fileId = viewMatch[1];
  
  // Format: https://drive.google.com/open?id={FILE_ID}
  const openMatch = url.match(/[?&]id=([a-zA-Z0-9_-]+)/);
  if (!fileId && openMatch) fileId = openMatch[1];
  
  // Format: https://drive.google.com/uc?id={FILE_ID}
  const ucMatch = url.match(/\/uc\?.*id=([a-zA-Z0-9_-]+)/);
  if (!fileId && ucMatch) fileId = ucMatch[1];
  
  if (fileId) {
    // Return direct download URL
    return `https://drive.google.com/uc?export=download&id=${fileId}`;
  }
  
  // If we can't parse it, return the original URL
  return url;
};
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Proctoring Score Card Component
function ProctoringCard({ icon: Icon, title, value, unit, status, description }) {
  const getStatusColor = () => {
    switch (status) {
      case 'good': return 'from-green-500/20 to-green-600/10 border-green-500/30';
      case 'warning': return 'from-yellow-500/20 to-yellow-600/10 border-yellow-500/30';
      case 'critical': return 'from-red-500/20 to-red-600/10 border-red-500/30';
      default: return 'from-slate-500/20 to-slate-600/10 border-slate-500/30';
    }
  };

  const getTextColor = () => {
    switch (status) {
      case 'good': return 'text-green-600 dark:text-green-400';
      case 'warning': return 'text-yellow-600 dark:text-yellow-400';
      case 'critical': return 'text-red-600 dark:text-red-400';
      default: return 'text-muted-foreground';
    }
  };

  const getIconBg = () => {
    switch (status) {
      case 'good': return 'bg-green-500/20';
      case 'warning': return 'bg-yellow-500/20';
      case 'critical': return 'bg-red-500/20';
      default: return 'bg-muted';
    }
  };

  return (
    <div className={`p-4 rounded-xl bg-gradient-to-br ${getStatusColor()} border`}>
      <div className="flex items-start justify-between mb-3">
        <div className={`p-2 rounded-lg ${getIconBg()}`}>
          <Icon className={`w-5 h-5 ${getTextColor()}`} />
        </div>
        {status === 'critical' && (
          <AlertCircle className="w-4 h-4 text-red-500 animate-pulse" />
        )}
      </div>
      <p className="text-muted-foreground text-xs uppercase tracking-wider mb-1">{title}</p>
      <div className="flex items-baseline gap-1">
        <span className={`text-2xl font-bold ${getTextColor()}`}>{value}</span>
        {unit && <span className="text-muted-foreground text-sm">{unit}</span>}
      </div>
      {description && (
        <p className="text-muted-foreground/70 text-xs mt-2">{description}</p>
      )}
    </div>
  );
}

// Integrity Score Ring Component
function IntegrityScoreRing({ score }) {
  const circumference = 2 * Math.PI * 45;
  const strokeDashoffset = circumference - (score / 100) * circumference;
  
  const getColor = () => {
    if (score >= 80) return '#22c55e';
    if (score >= 60) return '#eab308';
    return '#ef4444';
  };

  return (
    <div className="relative w-32 h-32">
      <svg className="w-32 h-32 transform -rotate-90">
        <circle
          cx="64"
          cy="64"
          r="45"
          className="stroke-muted"
          strokeWidth="8"
          fill="none"
        />
        <circle
          cx="64"
          cy="64"
          r="45"
          stroke={getColor()}
          strokeWidth="8"
          fill="none"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          className="transition-all duration-1000"
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-3xl font-bold text-foreground">{score}</span>
        <span className="text-xs text-muted-foreground">Integrity</span>
      </div>
    </div>
  );
}

// Video Player Modal Component - Custom player without Google Drive branding
function VideoPlayerModal({ isOpen, onClose, videoUrl, title }) {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [blobUrl, setBlobUrl] = useState(null);
  const videoRef = useRef(null);
  const { token } = useAuth();
  
  // Extract file ID from Google Drive URL
  const fileId = videoUrl ? videoUrl.match(/\/d\/([^/]+)/)?.[1] : null;
  const streamUrl = fileId ? `${API_URL}/api/video-stream/${fileId}?token=${token}` : null;
  const downloadUrl = fileId ? `https://drive.google.com/uc?export=download&id=${fileId}` : videoUrl;
  
  // Download video as blob for proper duration/seeking support
  useEffect(() => {
    if (!isOpen || !videoUrl) return;
    
    let cancelled = false;
    setIsLoading(true);
    setHasError(false);
    setBlobUrl(null);
    
    const tryFetch = async () => {
      // Try backend stream first (has proper auth for Google Drive)
      if (streamUrl) {
        try {
          const res = await fetch(streamUrl);
          if (res.ok) {
            const blob = await res.blob();
            if (!cancelled && blob.size > 0) {
              const url = URL.createObjectURL(blob);
              setBlobUrl(url);
              setIsLoading(false);
              return;
            }
          }
        } catch (err) {
          console.warn('Backend stream failed, trying direct URL:', err.message);
        }
      }
      
      // Fallback: try direct Google Drive download URL
      if (downloadUrl && !cancelled) {
        try {
          const res = await fetch(downloadUrl, { mode: 'cors' });
          if (res.ok) {
            const blob = await res.blob();
            if (!cancelled && blob.size > 0) {
              const url = URL.createObjectURL(blob);
              setBlobUrl(url);
              setIsLoading(false);
              return;
            }
          }
        } catch (err) {
          console.warn('Direct download also failed:', err.message);
        }
      }
      
      // All methods failed
      if (!cancelled) {
        setIsLoading(false);
        setHasError(true);
      }
    };
    
    tryFetch();
    
    return () => {
      cancelled = true;
      setBlobUrl(prev => { if (prev) URL.revokeObjectURL(prev); return null; });
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, videoUrl]);
  
  if (!isOpen || !videoUrl) return null;
  
  return (
    <Dialog open={isOpen} onOpenChange={(open) => {
      if (!open && blobUrl) URL.revokeObjectURL(blobUrl);
      onClose(open);
    }}>
      <DialogContent className="max-w-4xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Video className="w-5 h-5 text-blue-500" />
            {title || 'Video Recording'}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* Video Player Container */}
          <div className="relative bg-black rounded-lg overflow-hidden" style={{ aspectRatio: '16/9' }}>
            {/* Loading State */}
            {isLoading && !hasError && (
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-slate-900 z-10">
                <Loader2 className="w-12 h-12 text-blue-500 animate-spin mb-4" />
                <p className="text-white font-medium">Loading Video...</p>
                <p className="text-slate-400 text-sm mt-1">Downloading recording for playback</p>
              </div>
            )}
            
            {/* Error State */}
            {hasError && (
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-slate-900 z-10">
                <AlertTriangle className="w-12 h-12 text-yellow-500 mb-4" />
                <p className="text-white font-medium">Video Unavailable</p>
                <p className="text-slate-400 text-sm mt-1 text-center px-4">
                  Could not stream video. Try downloading instead.
                </p>
                <Button
                  className="mt-4 bg-green-500 hover:bg-green-600 text-white"
                  onClick={() => window.open(downloadUrl, '_blank')}
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download Video
                </Button>
              </div>
            )}
            
            {/* HTML5 Video Player — uses blob URL for proper duration/seeking */}
            {blobUrl && (
              <video
                ref={videoRef}
                src={blobUrl}
                controls
                controlsList="noremoteplayback"
                className="absolute inset-0 w-full h-full"
                preload="auto"
                style={{ backgroundColor: '#000' }}
              >
                Your browser does not support video playback.
              </video>
            )}
          </div>
          
          {/* Action Buttons */}
          <div className="flex justify-center gap-3 flex-wrap">
            <Button
              variant="outline"
              onClick={() => window.open(downloadUrl, '_blank')}
              className="border-green-500 text-green-600 dark:text-green-400"
            >
              <Download className="w-4 h-4 mr-2" />
              Download Video
            </Button>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Clock className="w-4 h-4" />
              <span>Use timeline bar to scrub through video</span>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// AI Analysis Modal Component
function AIAnalysisModal({ isOpen, onClose, interview }) {
  if (!isOpen || !interview) return null;
  
  const analysis = interview.ai_analysis;
  const proctoring = interview.proctoring_summary || {};
  const answers = interview.questions_answers || [];
  
  // Calculate metrics for display
  const avgKeyword = answers.length > 0 
    ? answers.reduce((sum, a) => sum + (a.keyword_score || 0), 0) / answers.length 
    : 0;
  const lowScoreQs = answers.filter(a => (a.score || 0) < 40).length;
  const emptyAnswers = answers.filter(a => !a.transcript || a.transcript === 'No speech detected').length;
  
  const getVerdictColor = (verdict) => {
    if (verdict?.includes('INTEGRITY')) return 'text-red-500 bg-red-500/20 border-red-500/50';
    if (verdict?.includes('CRITICAL')) return 'text-red-500 bg-red-500/20 border-red-500/50';
    if (verdict?.includes('ATTENTION')) return 'text-orange-500 bg-orange-500/20 border-orange-500/50';
    return 'text-yellow-500 bg-yellow-500/20 border-yellow-500/50';
  };
  
  const getRiskColor = (type) => {
    if (type === 'CRITICAL') return 'bg-red-500/20 text-red-400 border-red-500/50';
    if (type === 'HIGH') return 'bg-orange-500/20 text-orange-400 border-orange-500/50';
    return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/50';
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Brain className="w-5 h-5 text-purple-500" />
            AI Failure Analysis
          </DialogTitle>
          <DialogDescription>
            Detailed reasoning for why this interview was marked as failed
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-6">
          {/* Verdict Banner */}
          <div className={`p-4 rounded-xl border-2 ${getVerdictColor(analysis?.verdict || interview.failure_summary)}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <AlertTriangle className="w-6 h-6" />
                <div>
                  <h3 className="font-bold text-lg">{analysis?.verdict || 'FAIL'}</h3>
                  <p className="text-sm opacity-80">{analysis?.summary || interview.failure_summary}</p>
                </div>
              </div>
              {analysis?.confidence && (
                <Badge variant="outline" className="border-current">
                  {analysis.confidence} Confidence
                </Badge>
              )}
            </div>
          </div>
          
          {/* Quick Stats */}
          <div className="grid grid-cols-4 gap-3">
            <div className="p-3 bg-muted rounded-lg text-center">
              <p className="text-2xl font-bold text-foreground">{Math.round(interview.overall_score || 0)}%</p>
              <p className="text-xs text-muted-foreground">Overall Score</p>
            </div>
            <div className="p-3 bg-muted rounded-lg text-center">
              <p className="text-2xl font-bold text-foreground">{proctoring.eye_contact_percent || 0}%</p>
              <p className="text-xs text-muted-foreground">Eye Contact</p>
            </div>
            <div className="p-3 bg-muted rounded-lg text-center">
              <p className="text-2xl font-bold text-foreground">{Math.round(avgKeyword)}%</p>
              <p className="text-xs text-muted-foreground">Keyword Match</p>
            </div>
            <div className="p-3 bg-muted rounded-lg text-center">
              <p className="text-2xl font-bold text-foreground">{proctoring.face_match_percent || 100}%</p>
              <p className="text-xs text-muted-foreground">Face Match</p>
            </div>
            <div className={`p-3 rounded-lg text-center ${
              (proctoring.face_swap_events || 0) > 5 ? 'bg-red-500/20 border border-red-500/50' :
              (proctoring.face_swap_events || 0) > 0 ? 'bg-yellow-500/20 border border-yellow-500/50' : 'bg-muted'
            }`}>
              <p className={`text-2xl font-bold ${
                (proctoring.face_swap_events || 0) > 5 ? 'text-red-400' :
                (proctoring.face_swap_events || 0) > 0 ? 'text-yellow-400' : 'text-foreground'
              }`}>{proctoring.face_swap_events || 0}</p>
              <p className="text-xs text-muted-foreground">Face Swaps</p>
            </div>
            <div className={`p-4 rounded-xl text-center ${
              (proctoring.networkScore || proctoring.network_score || 100) >= 80 ? 'bg-green-500/20 border border-green-500/50' :
              (proctoring.networkScore || proctoring.network_score || 100) >= 50 ? 'bg-yellow-500/20 border border-yellow-500/50' : 'bg-red-500/20 border border-red-500/50'
            }`}>
              <p className={`text-2xl font-bold ${
                (proctoring.networkScore || proctoring.network_score || 100) >= 80 ? 'text-green-400' :
                (proctoring.networkScore || proctoring.network_score || 100) >= 50 ? 'text-yellow-400' : 'text-red-400'
              }`}>{proctoring.networkScore || proctoring.network_score || 'N/A'}%</p>
              <p className="text-xs text-muted-foreground">Connectivity</p>
            </div>
          </div>
          
          {/* Risk Factors */}
          {analysis?.risk_factors && analysis.risk_factors.length > 0 && (
            <div className="space-y-3">
              <h4 className="font-semibold text-foreground flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-red-500" />
                Risk Factors Identified
              </h4>
              <div className="space-y-2">
                {analysis.risk_factors.map((risk, idx) => (
                  <div key={idx} className={`p-3 rounded-lg border ${getRiskColor(risk.type)}`}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium">{risk.issue}</span>
                      <Badge variant="outline" className="text-xs border-current">{risk.type}</Badge>
                    </div>
                    <p className="text-sm opacity-80">{risk.detail}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Detailed Analysis Sections */}
          {analysis?.integrity_analysis && (
            <div className="p-4 bg-red-500/10 rounded-xl border border-red-500/30">
              <h4 className="font-semibold text-red-400 flex items-center gap-2 mb-3">
                <Shield className="w-4 h-4" />
                Integrity Analysis - {analysis.integrity_analysis.severity}
              </h4>
              <ul className="space-y-2">
                {analysis.integrity_analysis.issues.map((issue, idx) => (
                  <li key={idx} className="text-sm text-red-300 flex items-start gap-2">
                    <XCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    {issue}
                  </li>
                ))}
              </ul>
            </div>
          )}
          
          {analysis?.attention_analysis && (
            <div className="p-4 bg-orange-500/10 rounded-xl border border-orange-500/30">
              <h4 className="font-semibold text-orange-400 flex items-center gap-2 mb-3">
                <Eye className="w-4 h-4" />
                Attention Analysis
              </h4>
              <ul className="space-y-2">
                {analysis.attention_analysis.issues.map((issue, idx) => (
                  <li key={idx} className="text-sm text-orange-300 flex items-start gap-2">
                    <XCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    {issue}
                  </li>
                ))}
              </ul>
            </div>
          )}
          
          {analysis?.performance_analysis && (
            <div className="p-4 bg-yellow-500/10 rounded-xl border border-yellow-500/30">
              <h4 className="font-semibold text-yellow-400 flex items-center gap-2 mb-3">
                <Target className="w-4 h-4" />
                Performance Analysis
              </h4>
              <ul className="space-y-2">
                {analysis.performance_analysis.issues.map((issue, idx) => (
                  <li key={idx} className="text-sm text-yellow-300 flex items-start gap-2">
                    <XCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    {issue}
                  </li>
                ))}
              </ul>
              {analysis.performance_analysis.problematic_questions?.length > 0 && (
                <p className="text-xs text-yellow-400/70 mt-2">
                  Low-scoring questions: Q{analysis.performance_analysis.problematic_questions.join(', Q')}
                </p>
              )}
            </div>
          )}
          
          {/* Recommendations */}
          {analysis?.recommendations && analysis.recommendations.length > 0 && (
            <div className="p-4 bg-blue-500/10 rounded-xl border border-blue-500/30">
              <h4 className="font-semibold text-blue-400 flex items-center gap-2 mb-3">
                <Sparkles className="w-4 h-4" />
                Recommendations for Future
              </h4>
              <ul className="space-y-2">
                {analysis.recommendations.map((rec, idx) => (
                  <li key={idx} className="text-sm text-blue-300 flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    {rec}
                  </li>
                ))}
              </ul>
            </div>
          )}
          
          {/* Failure Reasons List */}
          {interview.failure_reasons && interview.failure_reasons.length > 0 && !analysis && (
            <div className="p-4 bg-muted rounded-xl">
              <h4 className="font-semibold text-foreground mb-3">Failure Criteria Triggered</h4>
              <ul className="space-y-2">
                {interview.failure_reasons.map((reason, idx) => (
                  <li key={idx} className="text-sm text-muted-foreground flex items-start gap-2">
                    <XCircle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                    {reason}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminInterviews() {
  const { token } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [interviews, setInterviews] = useState([]);
  const [universities, setUniversities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterResult, setFilterResult] = useState('all');
  const [selectedInterview, setSelectedInterview] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Video modal state
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const [currentVideoUrl, setCurrentVideoUrl] = useState('');
  const [currentVideoTitle, setCurrentVideoTitle] = useState('');
  
  // Selection states
  const [selectedIds, setSelectedIds] = useState([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [downloadingAll, setDownloadingAll] = useState(false);
  const [batchReminding, setBatchReminding] = useState(false);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  
  // Reminder filter
  const [filterReminder, setFilterReminder] = useState('all');
  
  // Engine filter (V1/V2/all)
  const [filterEngine, setFilterEngine] = useState('all');
  
  // AI Analysis Modal state
  const [analysisModalOpen, setAnalysisModalOpen] = useState(false);
  const [selectedAnalysis, setSelectedAnalysis] = useState(null);
  
  // Review/Approval states
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [reviewInterview, setReviewInterview] = useState(null);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [approving, setApproving] = useState(false);
  const [pendingReviewCount, setPendingReviewCount] = useState(0);
  const [answerOverrides, setAnswerOverrides] = useState([]); // Track individual answer edits
  const [reviewFormData, setReviewFormData] = useState({
    overall_score: 0,
    overall_result: 'pass',
    admin_notes: '',
    eye_contact_override: null,
    integrity_score_override: null,
    keyword_score_override: null,
    coherence_score_override: null
  });
  
  const openVideoModal = (url, title) => {
    setCurrentVideoUrl(url);
    setCurrentVideoTitle(title);
    setVideoModalOpen(true);
  };
  
  const openAnalysisModal = (interview) => {
    setSelectedAnalysis(interview);
    setAnalysisModalOpen(true);
  };
  
  const downloadAllVideos = async (interviewId, studentName) => {
    setDownloadingAll(true);
    try {
      toast.info('Preparing download... This may take a moment.');
      
      const response = await axios.get(
        `${API_URL}/api/admin/interview/${interviewId}/download-all-videos`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      // Create download link
      const blob = new Blob([response.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${studentName?.replace(/\s+/g, '_') || 'interview'}_videos.zip`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success('Download started!');
    } catch (error) {
      console.error('Download error:', error);
      if (error.response?.status === 404) {
        toast.error('No video recordings found for this interview');
      } else {
        toast.error('Failed to download videos. Please try again.');
      }
    } finally {
      setDownloadingAll(false);
    }
  };

  // Reassess interview with AI
  const [reassessing, setReassessing] = useState(null);
  
  const reassessInterview = async (interviewId, studentName) => {
    setReassessing(interviewId);
    try {
      toast.info(`Reassessing interview for ${studentName}... This may take a moment.`);
      
      const response = await axios.post(
        `${API_URL}/api/admin/interview/${interviewId}/full-reassess`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        toast.success(`Interview reassessed! New score: ${response.data.overall_score}%`);
        // Refresh interviews list
        fetchInterviews();
      }
    } catch (error) {
      console.error('Reassess error:', error);
      if (error.response?.status === 400) {
        toast.error(error.response.data?.detail || 'Interview must be completed for reassessment');
      } else {
        toast.error('Failed to reassess interview. Please try again.');
      }
    } finally {
      setReassessing(null);
    }
  };

  useEffect(() => {
    fetchInterviews();
    fetchUniversities();
    fetchPendingReviewCount();
  }, [filterStatus, filterResult]);

  // Auto-open interview detail when navigated from notifications with ?view=interviewId
  useEffect(() => {
    const viewId = searchParams.get('view');
    if (viewId && !loading && interviews.length > 0) {
      const found = interviews.find(i => i.id === viewId);
      if (found) {
        viewDetails(found);
      } else {
        // Interview not in current list — fetch directly
        (async () => {
          try {
            const response = await axios.get(`${API_URL}/api/admin/interview/${viewId}`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            setSelectedInterview(response.data);
            setActiveTab('overview');
            setDetailsOpen(true);
          } catch (err) {
            toast.error('Interview not found');
          }
        })();
      }
      // Clear the URL param so it doesn't re-trigger
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, loading, interviews]);

  const fetchPendingReviewCount = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/interviews/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPendingReviewCount(response.data.pending_review || 0);
    } catch (error) {
      console.error('Failed to fetch review stats');
    }
  };

  const fetchInterviews = async () => {
    try {
      const params = {};
      if (filterStatus !== 'all') params.status = filterStatus;
      if (filterResult !== 'all') params.result = filterResult;
      
      const response = await axios.get(`${API_URL}/api/interviews`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      setInterviews(response.data);
      setSelectedIds([]);
    } catch (error) {
      toast.error('Failed to fetch interviews');
    } finally {
      setLoading(false);
    }
  };

  const fetchUniversities = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/universities`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUniversities(response.data);
    } catch (error) {
      console.error('Failed to fetch universities');
    }
  };
  
  // Open review modal for an interview
  const openReviewModal = async (interview) => {
    setReviewLoading(true);
    setReviewModalOpen(true);
    try {
      const response = await axios.get(`${API_URL}/api/admin/interviews/${interview.id}/review`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReviewInterview(response.data);
      // Initialize form data with current values
      setReviewFormData({
        overall_score: response.data.overall_score || response.data.ai_suggested_score || 0,
        overall_result: response.data.overall_result || response.data.ai_suggested_result || 'pass',
        admin_notes: response.data.admin_notes || '',
        eye_contact_override: response.data.proctoring_summary?.eye_contact_percent || null,
        integrity_score_override: response.data.proctoring_summary?.integrity_score || null,
        keyword_score_override: response.data.ai_analysis?.keyword_score || null,
        coherence_score_override: response.data.ai_analysis?.coherence_score || null
      });
      // Initialize answer overrides from existing data
      const qa = response.data.questions_answers || [];
      setAnswerOverrides(qa.map((q, idx) => ({
        question_index: idx,
        score: q.admin_score_override || q.score || 0,
        keyword_score: q.keyword_score || 0,
        coherence_score: q.coherence_score || 0,
        notes: q.admin_notes || ''
      })));
    } catch (error) {
      toast.error('Failed to load interview details');
      setReviewModalOpen(false);
    } finally {
      setReviewLoading(false);
    }
  };
  
  // Update individual answer score
  const updateAnswerOverride = (index, field, value) => {
    setAnswerOverrides(prev => {
      const updated = [...prev];
      if (!updated[index]) {
        updated[index] = { question_index: index, score: 0, keyword_score: 0, coherence_score: 0, notes: '' };
      }
      updated[index][field] = value;
      return updated;
    });
  };
  
  // Recalculate overall score from answer overrides
  const recalculateOverallScore = () => {
    if (answerOverrides.length === 0) return;
    const avgScore = answerOverrides.reduce((sum, a) => sum + (a.score || 0), 0) / answerOverrides.length;
    setReviewFormData(prev => ({ ...prev, overall_score: Math.round(avgScore) }));
    toast.success(`Overall score recalculated: ${Math.round(avgScore)}%`);
  };
  
  // Save review changes
  const saveReviewChanges = async () => {
    if (!reviewInterview) return;
    try {
      // Include answer overrides in the save request
      const payload = {
        ...reviewFormData,
        answer_overrides: answerOverrides.map((a, idx) => ({
          question_index: idx,
          score: a.score,
          keyword_score: a.keyword_score,
          coherence_score: a.coherence_score,
          notes: a.notes
        }))
      };
      await axios.put(`${API_URL}/api/admin/interviews/${reviewInterview.id}/review`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Review changes saved');
      // Update local state
      setReviewInterview(prev => ({
        ...prev,
        overall_score: reviewFormData.overall_score,
        overall_result: reviewFormData.overall_result,
        admin_notes: reviewFormData.admin_notes
      }));
    } catch (error) {
      toast.error('Failed to save changes');
    }
  };
  
  // Approve interview and send results
  const approveInterview = async (sendEmail = true) => {
    if (!reviewInterview) return;
    setApproving(true);
    try {
      await axios.post(`${API_URL}/api/admin/interviews/${reviewInterview.id}/approve`, {
        final_verdict: reviewFormData.overall_result,
        admin_notes: reviewFormData.admin_notes,
        send_email: sendEmail
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Interview approved as ${reviewFormData.overall_result.toUpperCase()}${sendEmail ? ' - Email sent to student' : ''}`);
      setReviewModalOpen(false);
      fetchInterviews();
      fetchPendingReviewCount();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve interview');
    } finally {
      setApproving(false);
    }
  };
  
  // Reject interview
  const rejectInterview = async () => {
    if (!reviewInterview) return;
    setApproving(true);
    try {
      await axios.post(`${API_URL}/api/admin/interviews/${reviewInterview.id}/reject`, null, {
        headers: { Authorization: `Bearer ${token}` },
        params: { reason: reviewFormData.admin_notes }
      });
      toast.success('Interview rejected');
      setReviewModalOpen(false);
      fetchInterviews();
      fetchPendingReviewCount();
    } catch (error) {
      toast.error('Failed to reject interview');
    } finally {
      setApproving(false);
    }
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === paginatedInterviews.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(paginatedInterviews.map(i => i.id));
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    
    setDeleting(true);
    try {
      await axios.post(`${API_URL}/api/admin/interviews/bulk-delete`, 
        { ids: selectedIds },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${selectedIds.length} interview(s) deleted`);
      setDeleteDialogOpen(false);
      fetchInterviews();
    } catch (error) {
      toast.error('Failed to delete interviews');
    } finally {
      setDeleting(false);
    }
  };

  const viewDetails = async (interview) => {
    if (interview.engine_version === 'v2') { navigate(`/admin/v2/interviews/${interview.id}`); return; }
    try {
      const response = await axios.get(`${API_URL}/api/admin/interview/${interview.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedInterview(response.data);
      setActiveTab('overview');
      setDetailsOpen(true);
    } catch (error) {
      setSelectedInterview(interview);
      setActiveTab('overview');
      setDetailsOpen(true);
    }
    // Mark notification as read when viewing an interview
    try {
      await axios.post(`${API_URL}/api/admin/notifications/mark-read-by-interview/${interview.id}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (err) {
      // silently fail - not critical
    }
  };

  const sendReminder = async (interviewId) => {
    try {
      const response = await axios.post(`${API_URL}/api/admin/interviews/${interviewId}/send-reminder`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        toast.success(response.data.message);
        fetchInterviews();
      } else {
        toast.error(response.data.message || 'Failed to send reminder');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reminder');
    }
  };

  const sendBatchReminders = async () => {
    if (selectedIds.length === 0) return;
    setBatchReminding(true);
    try {
      const response = await axios.post(`${API_URL}/api/admin/interviews/batch-reminder`, 
        { interview_ids: selectedIds },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        toast.success(`${response.data.message}${response.data.skipped > 0 ? ` (${response.data.skipped} non-pending skipped)` : ''}`);
        setSelectedIds([]);
        // Refresh after a short delay to show updated counts for the first email
        setTimeout(() => fetchInterviews(), 3000);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start batch reminders');
    } finally {
      setBatchReminding(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      in_progress: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      completed: 'bg-green-500/20 text-green-400 border-green-500/30',
      cancelled: 'bg-red-500/20 text-red-400 border-red-500/30',
      under_review: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
      rejected: 'bg-red-500/20 text-red-400 border-red-500/30'
    };
    const labels = {
      under_review: 'Under Review',
      in_progress: 'In Progress'
    };
    return <Badge className={styles[status] || styles.pending}>{labels[status] || status?.replace('_', ' ')}</Badge>;
  };

  const getResultBadge = (result) => {
    if (!result) return <Badge className="bg-slate-500/20 text-slate-400">Pending</Badge>;
    const styles = {
      pass: 'bg-green-500/20 text-green-400 border-green-500/30',
      fail: 'bg-red-500/20 text-red-400 border-red-500/30',
      needs_review: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30'
    };
    return <Badge className={styles[result] || styles.needs_review}>{result?.replace('_', ' ')}</Badge>;
  };

  // Calculate integrity score from proctoring data
  const calculateIntegrityScore = (proctoring) => {
    if (!proctoring) return 100;
    
    let score = 100;
    
    // Deduct for tab switches (max -30)
    score -= Math.min(30, (proctoring.tab_switch_count || 0) * 10);
    
    // Deduct for low eye contact (max -20)
    const eyeContact = proctoring.eye_contact_percent || 100;
    if (eyeContact < 50) score -= 20;
    else if (eyeContact < 70) score -= 10;
    
    // Deduct for multiple faces (max -25)
    score -= Math.min(25, (proctoring.multiple_faces_count || 0) * 10);
    
    // Deduct for low face match (max -25)
    const faceMatch = proctoring.face_match_percent || 100;
    if (faceMatch < 50) score -= 25;
    else if (faceMatch < 70) score -= 15;
    else if (faceMatch < 85) score -= 5;
    
    // MAJOR DEDUCTION: Face swap events (different person detected)
    const faceSwapEvents = proctoring.face_swap_events || 0;
    if (faceSwapEvents > 10) score -= 40;
    else if (faceSwapEvents > 5) score -= 25;
    else if (faceSwapEvents > 0) score -= Math.min(15, faceSwapEvents * 3);
    
    // MAJOR DEDUCTION: Face absence is a serious integrity issue
    const faceAbsentCount = proctoring.face_absent_count || 0;
    if (faceAbsentCount > 0) {
      // Each face absence is -15 points, max -50
      score -= Math.min(50, faceAbsentCount * 15);
    }
    
    return Math.max(0, Math.round(score));
  };

  const getScoreStatus = (score, type) => {
    switch (type) {
      case 'tabSwitch':
        if (score === 0) return 'good';
        if (score <= 2) return 'warning';
        return 'critical';
      case 'percent':
        if (score >= 80) return 'good';
        if (score >= 60) return 'warning';
        return 'critical';
      case 'count':
        if (score === 0) return 'good';
        if (score <= 2) return 'warning';
        return 'critical';
      default:
        return 'good';
    }
  };

  const filteredInterviews = interviews.filter(interview => {
    const searchLower = search.toLowerCase();
    const matchesSearch = (
      interview.interview_number?.toLowerCase().includes(searchLower) ||
      interview.student_name?.toLowerCase().includes(searchLower) ||
      interview.student_email?.toLowerCase().includes(searchLower) ||
      interview.university_name?.toLowerCase().includes(searchLower)
    );
    const matchesReminder = filterReminder === 'all' || 
      (filterReminder === '5' ? (interview.reminders_sent || 0) >= 5 : (interview.reminders_sent || 0) === parseInt(filterReminder));
    const matchesEngine = filterEngine === 'all' || 
      (filterEngine === 'v2' ? interview.engine_version === 'v2' : !interview.engine_version || interview.engine_version === 'v1');
    return matchesSearch && matchesReminder && matchesEngine;
  });

  // Reset to page 1 when search/filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [search, filterStatus, filterResult, filterReminder]);

  // Pagination calculations
  const totalRecords = filteredInterviews.length;
  const totalPages = Math.ceil(totalRecords / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = Math.min(startIndex + pageSize, totalRecords);
  const paginatedInterviews = filteredInterviews.slice(startIndex, endIndex);

  return (
    <DashboardLayout role="super_admin">
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Interviews</h1>
            <p className="text-muted-foreground mt-1">View and manage all interview records</p>
          </div>
          
          {selectedIds.length > 0 && (
            <div className="flex items-center gap-2">
              <Button 
                variant="default"
                onClick={sendBatchReminders}
                disabled={batchReminding}
                className="bg-lime-500 hover:bg-lime-600 text-white"
                data-testid="batch-reminder-btn"
              >
                <Send className="w-4 h-4 mr-2" />
                {batchReminding ? 'Sending...' : `Send Reminders (${selectedIds.length})`}
              </Button>
              <Button 
                variant="destructive" 
                onClick={() => setDeleteDialogOpen(true)}
                className="bg-red-500 hover:bg-red-600"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete ({selectedIds.length})
              </Button>
            </div>
          )}
        </div>

        {/* Filters */}
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-4 items-center">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search by name, email, or university..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10"
                />
              </div>
              
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-40">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="under_review">Under Review ({pendingReviewCount})</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>

              <Select value={filterResult} onValueChange={setFilterResult}>
                <SelectTrigger className="w-40">
                  <Award className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Result" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Results</SelectItem>
                  <SelectItem value="pass">Pass</SelectItem>
                  <SelectItem value="fail">Fail</SelectItem>
                  <SelectItem value="needs_review">Needs Review</SelectItem>
                </SelectContent>
              </Select>

              <Select value={filterReminder} onValueChange={setFilterReminder}>
                <SelectTrigger className="w-44" data-testid="filter-reminder">
                  <Send className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Reminders" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Reminders</SelectItem>
                  <SelectItem value="0">0 reminders</SelectItem>
                  <SelectItem value="1">1 reminder</SelectItem>
                  <SelectItem value="2">2 reminders</SelectItem>
                  <SelectItem value="3">3 reminders</SelectItem>
                  <SelectItem value="4">4 reminders</SelectItem>
                  <SelectItem value="5">5+ reminders</SelectItem>
                </SelectContent>
              </Select>

              <Select value={filterEngine} onValueChange={setFilterEngine}>
                <SelectTrigger className="w-36" data-testid="filter-engine">
                  <SelectValue placeholder="Engine" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Engines</SelectItem>
                  <SelectItem value="v1">V1</SelectItem>
                  <SelectItem value="v2">V2</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card className="bg-card border-border">
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="border-border hover:bg-muted/50">
                  <TableHead className="w-12">
                    <Checkbox
                      checked={selectedIds.length === paginatedInterviews.length && paginatedInterviews.length > 0}
                      onCheckedChange={toggleSelectAll}
                    />
                  </TableHead>
                  <TableHead>ID</TableHead>
                  <TableHead>Student</TableHead>
                  <TableHead>University</TableHead>
                  <TableHead>Agent</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Reminders</TableHead>
                  <TableHead>AI Result</TableHead>
                  <TableHead>Final Verdict</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={11} className="text-center py-8 text-muted-foreground">
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : filteredInterviews.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={11} className="text-center py-8 text-muted-foreground">
                      No interviews found
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedInterviews.map((interview) => {
                    return (
                      <TableRow key={interview.id} className="border-border hover:bg-muted/50">
                        <TableCell>
                          <Checkbox
                            checked={selectedIds.includes(interview.id)}
                            onCheckedChange={() => toggleSelect(interview.id)}
                          />
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="font-mono text-xs">
                            {interview.interview_number || '-'}
                          </Badge>
                          {interview.engine_version === 'v2' && (
                            <Badge className="ml-1 text-[10px] bg-violet-500/20 text-violet-400 border-violet-500/40">V2</Badge>
                          )}
                          {interview.is_test_interview && (
                            <Badge variant="outline" className="ml-1 text-[10px] border-amber-500/40 text-amber-500">Test</Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="text-foreground font-medium">{interview.student_name}</p>
                            <p className="text-muted-foreground text-sm">{interview.student_email}</p>
                          </div>
                        </TableCell>
                        <TableCell className="text-foreground">{interview.university_name || '-'}</TableCell>
                        <TableCell className="text-foreground text-sm">{interview.agent_name || '-'}</TableCell>
                        <TableCell>{getStatusBadge(interview.status)}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1.5">
                            <Badge 
                              variant="outline" 
                              className={`text-xs ${
                                (interview.reminders_sent || 0) >= 3
                                  ? 'border-red-500/30 text-red-500' 
                                  : (interview.reminders_sent || 0) >= 1 
                                    ? 'border-amber-500/30 text-amber-500' 
                                    : 'border-slate-500/30 text-slate-400'
                              }`}
                              data-testid={`reminders-count-${interview.id}`}
                            >
                              {interview.reminders_sent || 0}
                            </Badge>
                            {interview.status === 'pending' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 w-7 p-0 text-muted-foreground hover:text-lime-500"
                                onClick={(e) => { e.stopPropagation(); sendReminder(interview.id); }}
                                title="Send reminder"
                                data-testid={`send-reminder-${interview.id}`}
                              >
                                <Send className="w-3.5 h-3.5" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          {/* AI Result - auto-populated after interview completion */}
                          {interview.ai_result || interview.ai_suggested_result || interview.overall_result ? (
                            <Badge className={
                              (interview.ai_result || interview.ai_suggested_result || interview.overall_result) === 'pass' 
                                ? 'bg-green-500/20 text-green-400 border-green-500/30' 
                                : 'bg-red-500/20 text-red-400 border-red-500/30'
                            }>
                              {(interview.ai_result || interview.ai_suggested_result || interview.overall_result)?.toUpperCase()}
                              {(interview.ai_suggested_score || interview.overall_score) ? ` (${Math.round(interview.ai_suggested_score || interview.overall_score)}%)` : ''}
                            </Badge>
                          ) : interview.status === 'completed' || interview.status === 'under_review' ? (
                            <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30">
                              Processing...
                            </Badge>
                          ) : (
                            <Badge className="bg-slate-500/20 text-slate-400">-</Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          {/* Final Verdict - set by admin after review */}
                          {interview.final_verdict ? (
                            <Badge className={
                              interview.final_verdict === 'pass' 
                                ? 'bg-green-500/30 text-green-300 border-green-400/50 font-bold' 
                                : 'bg-red-500/30 text-red-300 border-red-400/50 font-bold'
                            }>
                              {interview.final_verdict?.toUpperCase()}
                            </Badge>
                          ) : (interview.status === 'completed' || interview.status === 'under_review') ? (
                            <Badge className="bg-orange-500/30 text-orange-300 border-orange-400/50 animate-pulse">
                              ⚠️ NEEDS REVIEW
                            </Badge>
                          ) : (
                            <Badge className="bg-slate-500/20 text-slate-400">-</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-muted-foreground text-sm">
                          {interview.completed_at ? new Date(interview.completed_at).toLocaleDateString() : 
                           interview.created_at ? new Date(interview.created_at).toLocaleDateString() : '-'}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => viewDetails(interview)}
                              className="text-lime-600 dark:text-lime-400 hover:text-lime-700 dark:hover:text-lime-300 hover:bg-lime-500/10"
                            >
                              <Eye className="w-4 h-4 mr-1" />
                              View
                            </Button>
                            {/* Review/Edit button - available for all interviews except pending ones that haven't started */}
                            {(interview.status === 'under_review' || interview.status === 'completed' || interview.status === 'in_progress' || (interview.answers && interview.answers.length > 0)) && (
                              <Button
                                variant={!interview.admin_reviewed ? 'default' : 'outline'}
                                size="sm"
                                onClick={() => openReviewModal(interview)}
                                className={!interview.admin_reviewed 
                                  ? "bg-orange-500 hover:bg-orange-600 text-white animate-pulse" 
                                  : "border-blue-500 text-blue-500 hover:bg-blue-50"}
                                data-testid={`review-btn-${interview.id}`}
                              >
                                <CheckCircle2 className="w-4 h-4 mr-1" />
                                {!interview.admin_reviewed ? '⚡ Review Now' : 'Edit'}
                              </Button>
                            )}
                            {interview.status === 'completed' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => reassessInterview(interview.id, interview.student_name)}
                                disabled={reassessing === interview.id}
                                className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 hover:bg-blue-500/10"
                                title="Re-run AI assessment on this interview"
                              >
                                {reassessing === interview.id ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <RefreshCw className="w-4 h-4" />
                                )}
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </CardContent>
          
          {/* Pagination Footer */}
          {filteredInterviews.length > 0 && (
            <div className="px-6 py-4 border-t border-border flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                <span>Showing {startIndex + 1}-{endIndex} of {totalRecords} records</span>
                <div className="flex items-center gap-2">
                  <span>Per page:</span>
                  <select
                    value={pageSize}
                    onChange={(e) => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}
                    className="bg-background border border-border rounded-md px-2 py-1 text-sm"
                    data-testid="page-size-select"
                  >
                    {[10, 25, 50, 100].map(size => (
                      <option key={size} value={size}>{size}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="flex items-center gap-1" data-testid="pagination-controls">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                  className="h-8 px-2"
                >
                  First
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="h-8 px-3"
                >
                  Previous
                </Button>
                {/* Page numbers */}
                {(() => {
                  const pages = [];
                  let start = Math.max(1, currentPage - 2);
                  let end = Math.min(totalPages, start + 4);
                  if (end - start < 4) start = Math.max(1, end - 4);
                  for (let i = start; i <= end; i++) {
                    pages.push(
                      <Button
                        key={i}
                        variant={i === currentPage ? "default" : "outline"}
                        size="sm"
                        onClick={() => setCurrentPage(i)}
                        className={`h-8 w-8 p-0 ${i === currentPage ? 'bg-lime-500 text-white hover:bg-lime-600' : ''}`}
                      >
                        {i}
                      </Button>
                    );
                  }
                  return pages;
                })()}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="h-8 px-3"
                >
                  Next
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages}
                  className="h-8 px-2"
                >
                  Last
                </Button>
              </div>
            </div>
          )}
        </Card>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Confirm Deletion</DialogTitle>
            </DialogHeader>
            <p className="text-muted-foreground">
              Are you sure you want to delete {selectedIds.length} interview(s)? This action cannot be undone.
            </p>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                variant="destructive" 
                onClick={handleBulkDelete}
                disabled={deleting}
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Interview Details Dialog with Tabs */}
        <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
          <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-xl flex items-center gap-3">
                Interview Details
                {selectedInterview?.proctoring_summary && (
                  <Badge className={`${
                    calculateIntegrityScore(selectedInterview.proctoring_summary) >= 80 
                      ? 'bg-green-500/20 text-green-600 dark:text-green-400' 
                      : calculateIntegrityScore(selectedInterview.proctoring_summary) >= 60
                      ? 'bg-yellow-500/20 text-yellow-600 dark:text-yellow-400'
                      : 'bg-red-500/20 text-red-600 dark:text-red-400'
                  }`}>
                    <Shield className="w-3 h-3 mr-1" />
                    Integrity: {calculateIntegrityScore(selectedInterview.proctoring_summary)}%
                  </Badge>
                )}
              </DialogTitle>
            </DialogHeader>
            
            {selectedInterview && (
              <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="overview">
                    Overview
                  </TabsTrigger>
                  <TabsTrigger value="proctoring">
                    <Shield className="w-4 h-4 mr-2" />
                    Proctoring Analytics
                  </TabsTrigger>
                  <TabsTrigger value="answers">
                    Q&A Details
                  </TabsTrigger>
                </TabsList>

                {/* Overview Tab */}
                <TabsContent value="overview" className="space-y-4 mt-4">
                  {/* Student Info */}
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 p-4 bg-muted/50 rounded-xl">
                    <div>
                      <p className="text-muted-foreground text-sm">Student Name</p>
                      <p className="text-foreground font-medium">{selectedInterview.student_name}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground text-sm">Email</p>
                      <p className="text-foreground">{selectedInterview.student_email}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground text-sm">University</p>
                      <p className="text-foreground">{selectedInterview.university_name}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground text-sm">Status</p>
                      {getStatusBadge(selectedInterview.status)}
                    </div>
                    <div>
                      <p className="text-muted-foreground text-sm">Overall Score</p>
                      <p className={`text-2xl font-bold ${
                        selectedInterview.overall_score >= 70 ? 'text-green-600 dark:text-green-400' :
                        selectedInterview.overall_score >= 50 ? 'text-yellow-600 dark:text-yellow-400' : 'text-red-600 dark:text-red-400'
                      }`}>
                        {selectedInterview.overall_score ? `${Math.round(selectedInterview.overall_score)}%` : 'N/A'}
                      </p>
                    </div>
                    <div>
                      <p className="text-muted-foreground text-sm">Result</p>
                      {getResultBadge(selectedInterview.overall_result)}
                    </div>
                  </div>

                  {/* Recordings */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {selectedInterview.consent_recording && (
                      <div className="p-4 bg-muted/50 rounded-xl border border-border">
                        <h3 className="text-foreground font-semibold mb-3 flex items-center gap-2">
                          <Mic className="w-5 h-5 text-lime-500" />
                          Consent Recording
                        </h3>
                        <p className="text-muted-foreground text-sm mb-3">Duration: {selectedInterview.consent_recording.duration_seconds}s</p>
                        {selectedInterview.consent_recording.drive_url ? (
                          <div className="space-y-3">
                            {/* Audio Player */}
                            <audio 
                              controls 
                              className="w-full h-10"
                              src={selectedInterview.consent_recording.download_url || 
                                   `https://drive.google.com/uc?export=download&id=${selectedInterview.consent_recording.file_id}`}
                            >
                              Your browser does not support audio playback.
                            </audio>
                            <div className="flex gap-2">
                              <Button 
                                variant="outline" 
                                size="sm" 
                                className="border-green-500 text-green-600 dark:text-green-400"
                                onClick={() => {
                                  const url = selectedInterview.consent_recording.download_url || 
                                    `https://drive.google.com/uc?export=download&id=${selectedInterview.consent_recording.file_id}`;
                                  window.open(url, '_blank');
                                }}
                              >
                                <Download className="w-4 h-4 mr-2" />
                                Download
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <Badge className="bg-yellow-500/20 text-yellow-400">Pending Upload</Badge>
                        )}
                      </div>
                    )}

                    {selectedInterview.full_recording_url && (
                      <div className="p-4 bg-muted/50 rounded-xl border border-border">
                        <h3 className="text-foreground font-semibold mb-3 flex items-center gap-2">
                          <Video className="w-5 h-5 text-blue-500" />
                          Full Interview Recording
                        </h3>
                        <div className="flex gap-3 flex-wrap">
                          <Button 
                            onClick={() => openVideoModal(selectedInterview.full_recording_url, 'Full Interview Recording')}
                            className="bg-blue-500 hover:bg-blue-600 text-white"
                          >
                            <Play className="w-4 h-4 mr-2" />
                            Play Video
                          </Button>
                          <Button 
                            variant="outline" 
                            className="border-green-500 text-green-600 dark:text-green-400"
                            onClick={() => {
                              const fileId = selectedInterview.full_recording_url?.match(/\/d\/([^/]+)/)?.[1];
                              if (fileId) {
                                window.open(`https://drive.google.com/uc?export=download&id=${fileId}`, '_blank');
                              }
                            }}
                          >
                            <Download className="w-4 h-4 mr-2" />
                            Download
                          </Button>
                        </div>
                      </div>
                    )}
                    
                    {/* Download All Videos Section */}
                    {(selectedInterview.full_recording_url || selectedInterview.questions_answers?.some(qa => qa.video_url)) && (
                      <div className="p-4 bg-gradient-to-r from-purple-500/10 to-blue-500/10 rounded-xl border border-purple-500/30">
                        <h3 className="text-foreground font-semibold mb-3 flex items-center gap-2">
                          <FolderArchive className="w-5 h-5 text-purple-500" />
                          Download All Videos
                        </h3>
                        <p className="text-muted-foreground text-sm mb-3">
                          Download all interview recordings (full interview + individual answers) as a single ZIP file.
                        </p>
                        <Button 
                          onClick={() => downloadAllVideos(selectedInterview.id, selectedInterview.student_name)}
                          disabled={downloadingAll}
                          className="bg-purple-500 hover:bg-purple-600 text-white"
                          data-testid="download-all-videos-btn"
                        >
                          {downloadingAll ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Preparing ZIP...
                            </>
                          ) : (
                            <>
                              <FolderArchive className="w-4 h-4 mr-2" />
                              Download All as ZIP
                            </>
                          )}
                        </Button>
                      </div>
                    )}
                  </div>

                  {/* Student Feedback Section */}
                  {selectedInterview.student_feedback && (
                    <div className="p-4 bg-gradient-to-r from-muted/80 to-muted/50 rounded-xl border border-border">
                      <h3 className="text-foreground font-semibold mb-4 flex items-center gap-2">
                        <MessageSquare className="w-5 h-5 text-yellow-500" />
                        Student Feedback
                      </h3>
                      <div className="flex items-center gap-6 mb-4">
                        <div>
                          <p className="text-muted-foreground text-sm mb-1">Rating</p>
                          <div className="flex items-center gap-1">
                            {[1, 2, 3, 4, 5].map((star) => (
                              <Star
                                key={star}
                                className={`w-6 h-6 ${
                                  star <= selectedInterview.student_feedback.rating
                                    ? 'fill-yellow-400 text-yellow-400'
                                    : 'text-muted-foreground'
                                }`}
                              />
                            ))}
                            <span className="ml-2 text-foreground font-medium">
                              {selectedInterview.student_feedback.rating}/5
                            </span>
                          </div>
                        </div>
                        <div>
                          <p className="text-muted-foreground text-sm mb-1">Submitted At</p>
                          <p className="text-foreground text-sm">
                            {selectedInterview.student_feedback.submitted_at 
                              ? new Date(selectedInterview.student_feedback.submitted_at).toLocaleString() 
                              : 'N/A'}
                          </p>
                        </div>
                      </div>
                      {selectedInterview.student_feedback.feedback_text && (
                        <div>
                          <p className="text-muted-foreground text-sm mb-2">Comments</p>
                          <div className="p-3 bg-muted/60 rounded-lg border border-border">
                            <p className="text-foreground text-sm whitespace-pre-wrap">
                              {selectedInterview.student_feedback.feedback_text}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </TabsContent>

                {/* Proctoring Analytics Tab */}
                <TabsContent value="proctoring" className="space-y-6 mt-4">
                  {selectedInterview.proctoring_summary ? (
                    <>
                      {/* Integrity Score Header */}
                      <div className="flex items-center justify-between p-6 bg-gradient-to-r from-muted to-muted/80 rounded-xl border border-border">
                        <div>
                          <h3 className="text-foreground text-lg font-semibold mb-1">Interview Integrity Report</h3>
                          <p className="text-muted-foreground text-sm">
                            Completed on {selectedInterview.completed_at ? new Date(selectedInterview.completed_at).toLocaleString() : 'N/A'}
                          </p>
                        </div>
                        <IntegrityScoreRing score={calculateIntegrityScore(selectedInterview.proctoring_summary)} />
                      </div>

                      {/* Proctoring Cards Grid */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <ProctoringCard
                          icon={MonitorX}
                          title="Tab Switches"
                          value={selectedInterview.proctoring_summary.tab_switch_count || 0}
                          unit="times"
                          status={getScoreStatus(selectedInterview.proctoring_summary.tab_switch_count || 0, 'tabSwitch')}
                          description="Browser tab/window changes"
                        />
                        
                        <ProctoringCard
                          icon={EyeIcon}
                          title="Eye Contact"
                          value={selectedInterview.proctoring_summary.eye_contact_percent || 0}
                          unit="%"
                          status={getScoreStatus(selectedInterview.proctoring_summary.eye_contact_percent || 0, 'percent')}
                          description="Looking at camera"
                        />
                        
                        <ProctoringCard
                          icon={Users}
                          title="Multiple Faces"
                          value={selectedInterview.proctoring_summary.multiple_faces_count || 0}
                          unit="detections"
                          status={getScoreStatus(selectedInterview.proctoring_summary.multiple_faces_count || 0, 'count')}
                          description="Additional people detected"
                        />
                        
                        <ProctoringCard
                          icon={ScanFace}
                          title="Face Match"
                          value={selectedInterview.proctoring_summary.face_match_percent || 0}
                          unit="%"
                          status={getScoreStatus(selectedInterview.proctoring_summary.face_match_percent || 0, 'percent')}
                          description="Match with ID document"
                        />
                        
                        <ProctoringCard
                          icon={HeartPulse}
                          title="Liveness Score"
                          value={selectedInterview.proctoring_summary.liveness_score || 0}
                          unit="%"
                          status={getScoreStatus(selectedInterview.proctoring_summary.liveness_score || 0, 'percent')}
                          description="Real person verification"
                        />
                        
                        <ProctoringCard
                          icon={Waves}
                          title="Natural Tone"
                          value={selectedInterview.proctoring_summary.natural_tone_score || 0}
                          unit="%"
                          status={getScoreStatus(selectedInterview.proctoring_summary.natural_tone_score || 0, 'percent')}
                          description="Speech naturalness"
                        />
                        
                        <ProctoringCard
                          icon={Activity}
                          title="Blink Count"
                          value={selectedInterview.proctoring_summary.blink_count || 0}
                          unit="blinks"
                          status="good"
                          description="Eye movement activity"
                        />
                        
                        <ProctoringCard
                          icon={AlertTriangle}
                          title="Suspicious Patterns"
                          value={selectedInterview.proctoring_summary.suspicious_patterns || 0}
                          unit="flags"
                          status={getScoreStatus(selectedInterview.proctoring_summary.suspicious_patterns || 0, 'count')}
                          description="Anomalies detected"
                        />
                        
                        <ProctoringCard
                          icon={MonitorX}
                          title="Face Absent"
                          value={selectedInterview.proctoring_summary.face_absent_count || 0}
                          unit="times"
                          status={(selectedInterview.proctoring_summary.face_absent_count || 0) > 3 ? 'danger' : 
                                  (selectedInterview.proctoring_summary.face_absent_count || 0) > 0 ? 'warning' : 'good'}
                          description={`${selectedInterview.proctoring_summary.face_absent_duration || 0}s total absent`}
                        />
                      </div>

                      {/* Proctoring Events Log */}
                      {selectedInterview.proctoring_events?.length > 0 && (
                        <div className="p-4 bg-muted/50 rounded-xl border border-border">
                          <h3 className="text-foreground font-semibold mb-4 flex items-center gap-2">
                            <FileText className="w-5 h-5 text-orange-500" />
                            Proctoring Event Log ({selectedInterview.proctoring_events.length})
                          </h3>
                          <div className="space-y-2 max-h-48 overflow-y-auto">
                            {selectedInterview.proctoring_events.map((event, i) => (
                              <div key={i} className="flex items-center justify-between p-2 bg-muted rounded-lg text-sm">
                                <div className="flex items-center gap-2">
                                  <div className={`w-2 h-2 rounded-full ${
                                    event.event_type.includes('critical') ? 'bg-red-500' : 
                                    event.event_type.includes('warning') ? 'bg-yellow-500' : 'bg-blue-500'
                                  }`} />
                                  <span className="text-foreground capitalize">{event.event_type.replace(/_/g, ' ')}</span>
                                </div>
                                <span className="text-muted-foreground text-xs">
                                  {new Date(event.timestamp).toLocaleTimeString()}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Summary Assessment */}
                      <div className={`p-4 rounded-xl border ${
                        calculateIntegrityScore(selectedInterview.proctoring_summary) >= 80 
                          ? 'bg-green-500/10 border-green-500/30'
                          : calculateIntegrityScore(selectedInterview.proctoring_summary) >= 60
                          ? 'bg-yellow-500/10 border-yellow-500/30'
                          : 'bg-red-500/10 border-red-500/30'
                      }`}>
                        <h3 className={`font-semibold mb-2 flex items-center gap-2 ${
                          calculateIntegrityScore(selectedInterview.proctoring_summary) >= 80 
                            ? 'text-green-600 dark:text-green-400'
                            : calculateIntegrityScore(selectedInterview.proctoring_summary) >= 60
                            ? 'text-yellow-600 dark:text-yellow-400'
                            : 'text-red-600 dark:text-red-400'
                        }`}>
                          <Shield className="w-5 h-5" />
                          Integrity Assessment
                        </h3>
                        <p className="text-muted-foreground text-sm">
                          {calculateIntegrityScore(selectedInterview.proctoring_summary) >= 80 
                            ? 'This interview shows strong integrity indicators. No significant concerns detected during the session.'
                            : calculateIntegrityScore(selectedInterview.proctoring_summary) >= 60
                            ? 'This interview has some integrity concerns that may warrant review. Check the detailed metrics above.'
                            : 'This interview has significant integrity issues. Manual review is strongly recommended before making a decision.'
                          }
                        </p>
                      </div>
                    </>
                  ) : (
                    <div className="text-center py-12">
                      <Shield className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                      <p className="text-muted-foreground text-lg">No proctoring data available</p>
                      <p className="text-muted-foreground/70 text-sm mt-2">
                        Proctoring analytics are recorded during the interview session
                      </p>
                    </div>
                  )}
                </TabsContent>

                {/* Q&A Details Tab */}
                <TabsContent value="answers" className="space-y-4 mt-4">
                  <h3 className="text-foreground font-semibold flex items-center gap-2">
                    <FileText className="w-5 h-5 text-cyan-500" />
                    Questions & Answers ({selectedInterview.questions_answers?.length || 0})
                  </h3>
                  
                  {selectedInterview.questions_answers?.length > 0 ? (
                    selectedInterview.questions_answers.map((qa, index) => (
                      <QuestionAnswerCard 
                        key={index} 
                        qa={qa} 
                        index={index} 
                        onPlayVideo={openVideoModal}
                        interviewId={selectedInterview?.id}
                      />
                    ))
                  ) : (
                    <p className="text-muted-foreground text-center py-8">No answers recorded yet</p>
                  )}
                </TabsContent>
              </Tabs>
            )}
          </DialogContent>
        </Dialog>
        
        {/* Video Player Modal */}
        <VideoPlayerModal
          isOpen={videoModalOpen}
          onClose={() => setVideoModalOpen(false)}
          videoUrl={currentVideoUrl}
          title={currentVideoTitle}
        />
        
        {/* AI Analysis Modal */}
        <AIAnalysisModal
          isOpen={analysisModalOpen}
          onClose={() => setAnalysisModalOpen(false)}
          interview={selectedAnalysis}
        />
        
        {/* Admin Review Modal */}
        <Dialog open={reviewModalOpen} onOpenChange={setReviewModalOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-xl flex items-center gap-3">
                <CheckCircle2 className="w-6 h-6 text-orange-500" />
                Review Interview
              </DialogTitle>
              <DialogDescription>
                Review and approve this interview. Modify scores if needed before sending results to student.
              </DialogDescription>
            </DialogHeader>
            
            {reviewLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
                <span className="ml-3 text-muted-foreground">Loading interview details...</span>
              </div>
            ) : reviewInterview && (
              <div className="space-y-6">
                {/* Student Info */}
                <div className="grid grid-cols-2 gap-4 p-4 bg-muted/50 rounded-lg">
                  <div>
                    <p className="text-sm text-muted-foreground">Student</p>
                    <p className="font-medium">{reviewInterview.student_name}</p>
                    <p className="text-sm text-muted-foreground">{reviewInterview.student_email}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">University</p>
                    <p className="font-medium">{reviewInterview.university_name || 'N/A'}</p>
                    <p className="text-sm text-muted-foreground">Agent: {reviewInterview.agent_name || 'N/A'}</p>
                  </div>
                </div>
                
                {/* AI Suggested vs Admin Override */}
                <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                  <h4 className="font-medium text-blue-400 mb-2 flex items-center gap-2">
                    <Sparkles className="w-4 h-4" />
                    AI Suggested Result
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-muted-foreground">AI Score</p>
                      <p className="text-2xl font-bold">{reviewInterview.ai_suggested_score || reviewInterview.overall_score || 0}%</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">AI Verdict</p>
                      <Badge className={reviewInterview.ai_suggested_result === 'pass' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}>
                        {(reviewInterview.ai_suggested_result || reviewInterview.overall_result || 'pending').toUpperCase()}
                      </Badge>
                    </div>
                  </div>
                </div>
                
                {/* Editable Scores Section */}
                <div className="space-y-4">
                  <h4 className="font-semibold flex items-center gap-2">
                    <Target className="w-4 h-4 text-orange-500" />
                    Admin Override (Edit Values)
                  </h4>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm text-muted-foreground">Overall Score (%)</label>
                      <Input 
                        type="number"
                        min="0"
                        max="100"
                        value={reviewFormData.overall_score}
                        onChange={(e) => setReviewFormData(prev => ({...prev, overall_score: Number(e.target.value)}))}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <label className="text-sm text-muted-foreground">Final Verdict</label>
                      <Select 
                        value={reviewFormData.overall_result} 
                        onValueChange={(v) => setReviewFormData(prev => ({...prev, overall_result: v}))}
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pass">PASS</SelectItem>
                          <SelectItem value="fail">FAIL</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm text-muted-foreground">Eye Contact (%)</label>
                      <Input 
                        type="number"
                        min="0"
                        max="100"
                        value={reviewFormData.eye_contact_override || ''}
                        placeholder={reviewInterview.proctoring_summary?.eye_contact_percent || 'N/A'}
                        onChange={(e) => setReviewFormData(prev => ({...prev, eye_contact_override: e.target.value ? Number(e.target.value) : null}))}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <label className="text-sm text-muted-foreground">Integrity Score (%)</label>
                      <Input 
                        type="number"
                        min="0"
                        max="100"
                        value={reviewFormData.integrity_score_override || ''}
                        placeholder={reviewInterview.proctoring_summary?.integrity_score || 'N/A'}
                        onChange={(e) => setReviewFormData(prev => ({...prev, integrity_score_override: e.target.value ? Number(e.target.value) : null}))}
                        className="mt-1"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm text-muted-foreground">Keyword Score (%)</label>
                      <Input 
                        type="number"
                        min="0"
                        max="100"
                        value={reviewFormData.keyword_score_override || ''}
                        placeholder={reviewInterview.ai_analysis?.keyword_score || 'N/A'}
                        onChange={(e) => setReviewFormData(prev => ({...prev, keyword_score_override: e.target.value ? Number(e.target.value) : null}))}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <label className="text-sm text-muted-foreground">Coherence Score (%)</label>
                      <Input 
                        type="number"
                        min="0"
                        max="100"
                        value={reviewFormData.coherence_score_override || ''}
                        placeholder={reviewInterview.ai_analysis?.coherence_score || 'N/A'}
                        onChange={(e) => setReviewFormData(prev => ({...prev, coherence_score_override: e.target.value ? Number(e.target.value) : null}))}
                        className="mt-1"
                      />
                    </div>
                  </div>
                </div>
                
                {/* Admin Notes */}
                <div>
                  <label className="text-sm text-muted-foreground">Admin Notes (Optional)</label>
                  <textarea
                    className="mt-1 w-full p-3 border rounded-lg bg-background text-foreground min-h-[80px]"
                    placeholder="Add any notes about this review..."
                    value={reviewFormData.admin_notes}
                    onChange={(e) => setReviewFormData(prev => ({...prev, admin_notes: e.target.value}))}
                  />
                </div>
                
                {/* View Recordings Button */}
                {reviewInterview.full_recording_url && (
                  <Button
                    variant="outline"
                    onClick={() => {
                      setCurrentVideoUrl(reviewInterview.full_recording_url);
                      setCurrentVideoTitle(`Full Recording - ${reviewInterview.student_name}`);
                      setVideoModalOpen(true);
                    }}
                    className="w-full"
                  >
                    <Video className="w-4 h-4 mr-2" />
                    View Full Recording
                  </Button>
                )}
                
                {/* Questions & Answers - EDITABLE Section */}
                {reviewInterview.questions_answers?.length > 0 && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h4 className="font-semibold flex items-center gap-2">
                        <FileText className="w-4 h-4 text-purple-500" />
                        Question Answers Review ({reviewInterview.questions_answers.length} Questions)
                      </h4>
                      <Button variant="outline" size="sm" onClick={recalculateOverallScore}>
                        <RefreshCw className="w-3 h-3 mr-1" />
                        Recalculate Overall
                      </Button>
                    </div>
                    
                    <div className="max-h-[400px] overflow-y-auto space-y-4 border rounded-lg p-4 bg-muted/30">
                      {reviewInterview.questions_answers.map((qa, idx) => (
                        <div key={idx} className="p-4 bg-background rounded-lg border shadow-sm">
                          {/* Question Header */}
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex-1">
                              <p className="font-semibold text-sm flex items-center gap-2">
                                <span className="bg-purple-500 text-white px-2 py-0.5 rounded text-xs">Q{idx+1}</span>
                                {qa.question_text || 'Question not found'}
                              </p>
                              {qa.question_category && qa.question_category !== 'Uncategorized' && (
                                <p className="text-xs text-blue-400 mt-0.5 font-medium">Category: {qa.question_category}</p>
                              )}
                              {qa.expected_keywords?.length > 0 && (
                                <p className="text-xs text-muted-foreground mt-1">
                                  <strong>Expected Keywords:</strong> {qa.expected_keywords.join(', ')}
                                </p>
                              )}
                            </div>
                            {qa.video_url && (
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => {
                                  setCurrentVideoUrl(qa.video_url);
                                  setCurrentVideoTitle(`Q${idx+1} Answer - ${reviewInterview.student_name}`);
                                  setVideoModalOpen(true);
                                }}
                              >
                                <Play className="w-4 h-4 mr-1" />
                                Watch
                              </Button>
                            )}
                          </div>
                          
                          {/* Student's Answer */}
                          <div className="bg-slate-50 dark:bg-slate-800 p-3 rounded-lg mb-3">
                            <p className="text-xs text-muted-foreground mb-1 font-medium">Student's Answer:</p>
                            <p className="text-sm">{qa.transcript || qa.transcription || qa.answer_text || 'No transcription available'}</p>
                          </div>
                          
                          {/* AI Scores (Read-only reference) */}
                          <div className="grid grid-cols-4 gap-2 mb-3 p-2 bg-blue-50 dark:bg-blue-900/20 rounded text-xs">
                            <div>
                              <span className="text-muted-foreground">AI Score:</span>
                              <span className="ml-1 font-bold">{qa.score || 0}%</span>
                            </div>
                            <div>
                              <span className="text-muted-foreground">Keyword:</span>
                              <span className="ml-1 font-bold">{qa.keyword_score || 0}%</span>
                            </div>
                            <div>
                              <span className="text-muted-foreground">Coherence:</span>
                              <span className="ml-1 font-bold">{qa.coherence_score || 0}%</span>
                            </div>
                            <div>
                              <span className="text-muted-foreground">Eye Contact:</span>
                              <span className="ml-1 font-bold">{qa.eye_contact_percent || 0}%</span>
                            </div>
                          </div>
                          
                          {/* Admin Override Inputs */}
                          <div className="grid grid-cols-4 gap-2">
                            <div>
                              <label className="text-xs text-orange-600 font-medium">Override Score</label>
                              <Input 
                                type="number"
                                min="0"
                                max="100"
                                value={answerOverrides[idx]?.score || ''}
                                placeholder={qa.score || '0'}
                                onChange={(e) => updateAnswerOverride(idx, 'score', Number(e.target.value))}
                                className="h-8 text-sm"
                              />
                            </div>
                            <div>
                              <label className="text-xs text-orange-600 font-medium">Override Keyword</label>
                              <Input 
                                type="number"
                                min="0"
                                max="100"
                                value={answerOverrides[idx]?.keyword_score || ''}
                                placeholder={qa.keyword_score || '0'}
                                onChange={(e) => updateAnswerOverride(idx, 'keyword_score', Number(e.target.value))}
                                className="h-8 text-sm"
                              />
                            </div>
                            <div>
                              <label className="text-xs text-orange-600 font-medium">Override Coherence</label>
                              <Input 
                                type="number"
                                min="0"
                                max="100"
                                value={answerOverrides[idx]?.coherence_score || ''}
                                placeholder={qa.coherence_score || '0'}
                                onChange={(e) => updateAnswerOverride(idx, 'coherence_score', Number(e.target.value))}
                                className="h-8 text-sm"
                              />
                            </div>
                            <div>
                              <label className="text-xs text-orange-600 font-medium">Quick Mark</label>
                              <div className="flex gap-1">
                                <Button 
                                  variant="outline" 
                                  size="sm" 
                                  className="h-8 flex-1 text-green-600 hover:bg-green-50"
                                  onClick={() => updateAnswerOverride(idx, 'score', 100)}
                                >
                                  ✓
                                </Button>
                                <Button 
                                  variant="outline" 
                                  size="sm" 
                                  className="h-8 flex-1 text-red-600 hover:bg-red-50"
                                  onClick={() => updateAnswerOverride(idx, 'score', 0)}
                                >
                                  ✗
                                </Button>
                              </div>
                            </div>
                          </div>
                          
                          {/* Admin Notes for this answer */}
                          <div className="mt-2">
                            <Input 
                              placeholder="Add notes for this answer..."
                              value={answerOverrides[idx]?.notes || ''}
                              onChange={(e) => updateAnswerOverride(idx, 'notes', e.target.value)}
                              className="h-8 text-sm"
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
            
            <DialogFooter className="gap-2 mt-4 flex-wrap">
              <Button variant="outline" onClick={() => setReviewModalOpen(false)}>
                Cancel
              </Button>
              <Button variant="outline" onClick={saveReviewChanges}>
                Save Changes
              </Button>
              <Button 
                variant="destructive"
                onClick={rejectInterview}
                disabled={approving}
              >
                {approving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <XCircle className="w-4 h-4 mr-2" />}
                Reject
              </Button>
              <Button 
                onClick={() => approveInterview(false)}
                disabled={approving}
                variant="outline"
                className="border-green-500 text-green-500 hover:bg-green-500/10"
              >
                {approving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                Approve (No Email)
              </Button>
              <Button 
                onClick={() => approveInterview(true)}
                disabled={approving}
                className="bg-green-500 hover:bg-green-600 text-white"
              >
                {approving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                Approve & Send Email
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}

// Question Answer Card Component
function QuestionAnswerCard({ qa, index, onPlayVideo, interviewId }) {
  const [expanded, setExpanded] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [extractedTranscript, setExtractedTranscript] = useState(qa.extracted_transcript || null);
  const { token } = useAuth();
  
  const getScoreColor = (score) => {
    if (score >= 70) return 'text-green-600 dark:text-green-400';
    if (score >= 50) return 'text-yellow-600 dark:text-yellow-400';
    return 'text-red-600 dark:text-red-400';
  };

  const getScoreBg = (score) => {
    if (score >= 70) return 'bg-green-500/20';
    if (score >= 50) return 'bg-yellow-500/20';
    return 'bg-red-500/20';
  };
  
  // Get per-question eye contact from proctoring snapshot
  const questionEyeContact = qa.proctoring_snapshot?.questionEyeContactPercent ?? 
                             qa.proctoring_snapshot?.eyeContactPercent ?? null;
  
  const handleExtractTranscript = async () => {
    if (!interviewId) return;
    setTranscribing(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/admin/interview/${interviewId}/transcribe-question/${index}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        setExtractedTranscript(response.data.extracted_transcript);
        toast.success(`Transcript extracted for Q${index + 1}`);
      }
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to extract transcript';
      toast.error(msg);
    } finally {
      setTranscribing(false);
    }
  };
  
  return (
    <div className="p-4 bg-muted/50 rounded-xl border border-border">
      <div 
        className="flex items-start justify-between cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <span className="text-lime-600 dark:text-lime-400 font-bold">Q{index + 1}</span>
            <span className={`text-sm px-2 py-0.5 rounded ${
              qa.passed ? 'bg-green-500/20 text-green-600 dark:text-green-400' : 'bg-red-500/20 text-red-600 dark:text-red-400'
            }`}>
              {qa.score?.toFixed(0)}%
            </span>
            {/* Per-question Eye Contact Badge */}
            {questionEyeContact !== null && (
              <span className={`text-xs px-2 py-0.5 rounded flex items-center gap-1 ${
                questionEyeContact >= 70 ? 'bg-green-500/20 text-green-600 dark:text-green-400' :
                questionEyeContact >= 40 ? 'bg-yellow-500/20 text-yellow-600 dark:text-yellow-400' :
                'bg-red-500/20 text-red-600 dark:text-red-400'
              }`}>
                <Eye className="w-3 h-3" />
                Eye: {questionEyeContact}%
              </span>
            )}
            {qa.exact_match_detected && (
              <span className="text-xs px-2 py-0.5 rounded bg-red-500/30 text-red-600 dark:text-red-400 border border-red-500/50 animate-pulse">
                PLAGIARISM DETECTED
              </span>
            )}
          </div>
          <p className="text-foreground font-medium">{qa.question_text}</p>
          {qa.question_category && qa.question_category !== 'Uncategorized' && (
            <p className="text-xs text-blue-400 font-medium mt-0.5">Category: {qa.question_category}</p>
          )}
        </div>
        {expanded ? <ChevronUp className="w-5 h-5 text-muted-foreground" /> : <ChevronDown className="w-5 h-5 text-muted-foreground" />}
      </div>
      
      {expanded && (
        <div className="mt-4 pt-4 border-t border-border space-y-4">
          {/* Analysis Cards Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {/* Eye Contact Card (Per Question) */}
            {questionEyeContact !== null && (
              <div className={`p-3 rounded-lg ${getScoreBg(questionEyeContact)} border border-border`}>
                <div className="flex items-center gap-2 mb-1">
                  <Eye className="w-4 h-4 text-blue-500" />
                  <span className="text-xs text-muted-foreground">Eye Contact</span>
                </div>
                <span className={`text-xl font-bold ${getScoreColor(questionEyeContact)}`}>
                  {questionEyeContact}%
                </span>
              </div>
            )}
            
            {/* Keyword Score Card */}
            <div className={`p-3 rounded-lg ${getScoreBg(qa.keyword_score || 0)} border border-border`}>
              <div className="flex items-center gap-2 mb-1">
                <Target className="w-4 h-4 text-cyan-500" />
                <span className="text-xs text-muted-foreground">Keyword Match</span>
              </div>
              <span className={`text-xl font-bold ${getScoreColor(qa.keyword_score || 0)}`}>
                {(qa.keyword_score || 0).toFixed(0)}%
              </span>
            </div>

            {/* Relevancy Score Card */}
            <div className={`p-3 rounded-lg ${getScoreBg(qa.relevancy_score || 0)} border border-border`}>
              <div className="flex items-center gap-2 mb-1">
                <Sparkles className="w-4 h-4 text-purple-500" />
                <span className="text-xs text-muted-foreground">AI Relevancy</span>
              </div>
              <span className={`text-xl font-bold ${getScoreColor(qa.relevancy_score || 0)}`}>
                {(qa.relevancy_score || 0).toFixed(0)}%
              </span>
            </div>

            {/* Exact Match Card */}
            <div className={`p-3 rounded-lg ${qa.exact_match_detected ? 'bg-red-500/20' : 'bg-green-500/20'} border border-border`}>
              <div className="flex items-center gap-2 mb-1">
                <Copy className="w-4 h-4 text-orange-500" />
                <span className="text-xs text-muted-foreground">Plagiarism Check</span>
              </div>
              <div className="flex items-center gap-1">
                {qa.exact_match_detected ? (
                  <>
                    <XCircle className="w-4 h-4 text-red-500" />
                    <span className="text-red-600 dark:text-red-400 font-bold text-sm">
                      {(qa.exact_match_similarity || 0).toFixed(0)}% Match
                    </span>
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="w-4 h-4 text-green-500" />
                    <span className="text-green-600 dark:text-green-400 font-bold text-sm">Original</span>
                  </>
                )}
              </div>
            </div>

            {/* Communication Score Card (from AI) */}
            <div className={`p-3 rounded-lg ${getScoreBg(qa.ai_analysis?.communication_score || 0)} border border-border`}>
              <div className="flex items-center gap-2 mb-1">
                <MessageSquare className="w-4 h-4 text-blue-500" />
                <span className="text-xs text-muted-foreground">Communication</span>
              </div>
              <span className={`text-xl font-bold ${getScoreColor(qa.ai_analysis?.communication_score || 0)}`}>
                {(qa.ai_analysis?.communication_score || 0).toFixed(0)}%
              </span>
            </div>
          </div>

          {/* AI Analysis Details */}
          {qa.ai_analysis && (
            <div className="p-3 bg-muted/50 rounded-lg border border-border">
              <h4 className="text-sm font-semibold text-purple-600 dark:text-purple-400 mb-2 flex items-center gap-2">
                <Brain className="w-4 h-4" />
                AI Analysis
              </h4>
              <div className="grid grid-cols-2 gap-3 mb-3">
                <div>
                  <span className="text-xs text-muted-foreground">Completeness</span>
                  <p className={`font-bold ${getScoreColor(qa.ai_analysis.completeness_score || 0)}`}>
                    {(qa.ai_analysis.completeness_score || 0).toFixed(0)}%
                  </p>
                </div>
                <div>
                  <span className="text-xs text-muted-foreground">Confidence</span>
                  <p className={`font-bold ${getScoreColor(qa.ai_analysis.confidence_score || 0)}`}>
                    {(qa.ai_analysis.confidence_score || 0).toFixed(0)}%
                  </p>
                </div>
              </div>
              {qa.ai_analysis.reading_detected && (
                <div className="p-2 bg-yellow-500/10 border border-yellow-500/30 rounded text-yellow-600 dark:text-yellow-400 text-xs mb-2">
                  ⚠️ Reading from script detected
                </div>
              )}
              {qa.ai_analysis.overall_assessment && (
                <p className="text-muted-foreground text-sm">{qa.ai_analysis.overall_assessment}</p>
              )}
              {qa.ai_analysis.missing_points?.length > 0 && (
                <div className="mt-2">
                  <span className="text-xs text-red-600 dark:text-red-400">Missing Points:</span>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {qa.ai_analysis.missing_points.map((point, i) => (
                      <Badge key={i} variant="outline" className="text-xs text-red-600 dark:text-red-400 border-red-500/30">
                        {point}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Transcript Section */}
          <div className="space-y-3">
            {/* Live Transcript */}
            <div>
              <p className="text-muted-foreground text-sm mb-2 flex items-center gap-2">
                <Mic className="w-3.5 h-3.5" />
                Live Transcript:
              </p>
              <div className="p-3 bg-muted rounded-lg max-h-40 overflow-y-auto">
                <p className="text-foreground text-sm leading-relaxed">
                  {qa.transcript || 'No transcript available'}
                </p>
              </div>
            </div>
            
            {/* Extracted Transcript */}
            {extractedTranscript && (
              <div>
                <p className="text-sm mb-2 flex items-center gap-2 text-blue-600 dark:text-blue-400">
                  <FileText className="w-3.5 h-3.5" />
                  Extracted Transcript (Whisper):
                </p>
                <div className="p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg max-h-40 overflow-y-auto">
                  <p className="text-foreground text-sm leading-relaxed">
                    {extractedTranscript}
                  </p>
                </div>
              </div>
            )}
            
            {/* Extract Transcript Button */}
            {qa.video_url && (
              <Button
                size="sm"
                variant="outline"
                onClick={(e) => {
                  e.stopPropagation();
                  handleExtractTranscript();
                }}
                disabled={transcribing}
                className="text-blue-600 dark:text-blue-400 border-blue-500/50 hover:bg-blue-500/10"
                data-testid={`extract-transcript-q${index + 1}`}
              >
                {transcribing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Extracting Transcript...
                  </>
                ) : extractedTranscript ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Re-extract Transcript
                  </>
                ) : (
                  <>
                    <FileText className="w-4 h-4 mr-2" />
                    Extract Transcript from Video
                  </>
                )}
              </Button>
            )}
          </div>
          
          {qa.video_url && (
            <div>
              <p className="text-muted-foreground text-sm mb-2">Answer Recording:</p>
              <div className="flex gap-2 flex-wrap">
                <Button 
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (onPlayVideo) {
                      onPlayVideo(qa.video_url, `Question ${index + 1} Answer`);
                    }
                  }}
                  className="bg-blue-500 hover:bg-blue-600 text-white"
                  data-testid={`play-video-q${index + 1}`}
                >
                  <Play className="w-4 h-4 mr-2" />
                  Play Video
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="text-green-600 dark:text-green-400 border-green-500/50"
                  onClick={(e) => {
                    e.stopPropagation();
                    const fileId = qa.video_url.match(/\/d\/([^/]+)/)?.[1];
                    if (fileId) {
                      window.open(`https://drive.google.com/uc?export=download&id=${fileId}`, '_blank');
                    }
                  }}
                  data-testid={`download-video-q${index + 1}`}
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download
                </Button>
              </div>
            </div>
          )}
          
          {qa.keywords_found?.length > 0 && (
            <div>
              <p className="text-muted-foreground text-sm mb-2">Keywords Detected ({qa.keywords_found.length}):</p>
              <div className="flex flex-wrap gap-2">
                {qa.keywords_found.map((kw, i) => (
                  <Badge key={i} className="bg-lime-500/20 text-lime-600 dark:text-lime-400 border-lime-500/30">
                    {kw}
                  </Badge>
                ))}
              </div>
            </div>
          )}
          
          <div className="flex gap-4 text-sm text-muted-foreground">
            <span>Duration: {qa.duration}s</span>
            <span>Submitted: {qa.submitted_at ? new Date(qa.submitted_at).toLocaleString() : 'N/A'}</span>
          </div>
        </div>
      )}
    </div>
  );
}
